/**
 * Slowpokes Coffee Finder — Help Me Choose
 * Weighted recommendations from Theme Editor product mappings.
 */
(function () {
  'use strict';

  var FLAVOR_PRIMARY = 5;
  var FLAVOR_SECONDARY = 2;
  var PERSONALITY = 3;

  /**
   * Scoring profiles keyed to Theme Editor product slots.
   * Primary flavor = +5, secondary = +2, personality = +3.
   */
  var SCORE = {
    colombia_excelso: {
      flavor: { chocolatey_nutty: FLAVOR_PRIMARY, just_smooth: FLAVOR_PRIMARY },
      personality: { classic: PERSONALITY },
    },
    guatemala: {
      flavor: { chocolatey_nutty: FLAVOR_PRIMARY },
      personality: { classic: PERSONALITY },
    },
    honduras: {
      flavor: { chocolatey_nutty: FLAVOR_PRIMARY },
      personality: { classic: PERSONALITY },
    },
    brazil: {
      flavor: {
        chocolatey_nutty: FLAVOR_PRIMARY,
        sweet_caramel: FLAVOR_PRIMARY,
      },
      personality: { different: PERSONALITY },
    },
    costa_rica: {
      flavor: {
        sweet_caramel: FLAVOR_PRIMARY,
        just_smooth: FLAVOR_PRIMARY,
      },
      personality: { classic: PERSONALITY },
    },
    mexico_chiapas: {
      flavor: {
        sweet_caramel: FLAVOR_PRIMARY,
        fruity_chocolatey: FLAVOR_PRIMARY,
      },
      personality: { different: PERSONALITY, surprise: FLAVOR_SECONDARY },
    },
    ethiopia: {
      flavor: { fruity_chocolatey: FLAVOR_PRIMARY },
      personality: {
        surprise: PERSONALITY,
        different: PERSONALITY,
      },
    },
    colombia_decaf: {
      flavor: {},
      personality: {},
    },
  };

  /**
   * Deterministic Q1 tie-break order (closest tasting-note match first).
   */
  var TIE_ORDER = {
    chocolatey_nutty: [
      'colombia_excelso',
      'guatemala',
      'honduras',
      'brazil',
      'costa_rica',
      'mexico_chiapas',
      'ethiopia',
    ],
    sweet_caramel: [
      'costa_rica',
      'mexico_chiapas',
      'brazil',
      'colombia_excelso',
      'guatemala',
      'honduras',
      'ethiopia',
    ],
    fruity_chocolatey: [
      'ethiopia',
      'mexico_chiapas',
      'costa_rica',
      'brazil',
      'colombia_excelso',
      'guatemala',
      'honduras',
    ],
    just_smooth: [
      'colombia_excelso',
      'costa_rica',
      'brazil',
      'guatemala',
      'honduras',
      'mexico_chiapas',
      'ethiopia',
    ],
  };

  var DEFAULT_NOTES = {
    colombia_excelso: 'Chocolate · Hazelnut',
    guatemala: 'Dark Chocolate · Almond',
    honduras: 'Dark Chocolate · Almond',
    brazil: 'Chocolate · Honey · Pecan',
    costa_rica: 'Honey · Chocolate',
    mexico_chiapas: 'Milk Chocolate · Caramel · Sweet Grape',
    ethiopia: 'Milk Chocolate · Blueberry',
    colombia_decaf: 'Apple · Caramel · Chocolate',
  };

  function parseConfig(root) {
    var node = root.querySelector('[data-hp-finder-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function indexProducts(list) {
    var map = {};
    (list || []).forEach(function (item) {
      if (item && item.id) map[item.id] = item;
    });
    return map;
  }

  function scoreCoffee(id, flavor, personality) {
    var profile = SCORE[id];
    if (!profile) return 0;
    var total = 0;
    if (flavor && profile.flavor && profile.flavor[flavor]) {
      total += profile.flavor[flavor];
    }
    if (personality && profile.personality && profile.personality[personality]) {
      total += profile.personality[personality];
    }
    return total;
  }

  function tieRank(id, flavor) {
    var order = TIE_ORDER[flavor] || [];
    var idx = order.indexOf(id);
    return idx === -1 ? 999 : idx;
  }

  function compareCandidates(a, b, flavor) {
    if (b.score !== a.score) return b.score - a.score;
    return tieRank(a.id, flavor) - tieRank(b.id, flavor);
  }

  function isShopable(product) {
    return !!(product && product.handle && product.url && product.available !== false);
  }

  function isMapped(product) {
    return !!(product && product.handle && product.url);
  }

  function buildCopy(id, flavor, personality, notes) {
    var noteLine = notes || DEFAULT_NOTES[id] || '';
    var lines = {
      colombia_excelso:
        'Balanced, familiar, and easy to love — an everyday Slowpokes cup built on ' +
        noteLine.toLowerCase() +
        '.',
      guatemala:
        'A classic chocolate-and-nut cup. ' + noteLine + ' keeps things rich and familiar.',
      honduras:
        'Chocolate-forward and nutty, the kind of coffee that feels like home. ' + noteLine + '.',
      brazil:
        'Chocolatey and nutty, with a sweeter turn. ' +
        noteLine +
        ' give you comfort with a little extra character.',
      costa_rica:
        'Smooth, sweet, and approachable. ' + noteLine + ' make this an easy everyday pour.',
      mexico_chiapas:
        'Sweet and chocolatey with a fruitier side. ' +
        noteLine +
        ' keep the cup familiar — and a little more interesting.',
      ethiopia:
        'Familiar chocolate with a brighter fruit note. ' +
        noteLine +
        ' for when you want something grounded, but not boring.',
      colombia_decaf:
        'All the comfort, none of the buzz. ' +
        noteLine +
        ' in a sweet, balanced Swiss Water decaf.',
    };

    var base = lines[id] || ('A Slowpokes match featuring ' + noteLine + '.');

    if (id === 'colombia_decaf') {
      if (flavor === 'fruity_chocolatey') {
        return 'You asked for fruit with chocolate — and no caffeine. Colombia Decaf brings apple, caramel, and chocolate in a balanced cup.';
      }
      if (flavor === 'sweet_caramel') {
        return 'You like a little sweetness. Colombia Decaf keeps things smooth with apple, caramel, and chocolate — without the buzz.';
      }
      if (flavor === 'chocolatey_nutty' || flavor === 'just_smooth') {
        return 'Comforting and easy, just without the caffeine. Colombia Decaf stays balanced with apple, caramel, and chocolate.';
      }
      return base;
    }

    if (personality === 'surprise' && id === 'ethiopia') {
      return 'You wanted the most interesting option. Ethiopia keeps milk chocolate in the base while blueberry brings the adventure.';
    }
    if (personality === 'different' && id === 'brazil') {
      return 'Same chocolate-and-nut comfort you asked for, with honey and pecan for a slightly sweeter twist.';
    }
    if (personality === 'different' && id === 'mexico_chiapas') {
      return 'Sweet and chocolatey, with sweet grape for a little personality on top of the caramel.';
    }
    if (personality === 'classic' && (id === 'colombia_excelso' || id === 'costa_rica')) {
      return base;
    }

    return base;
  }

  function shopLabel(prefix, title) {
    var p = (prefix || 'Shop').trim();
    var t = (title || '').trim();
    if (!t) return p;
    return p + ' ' + t;
  }

  function initFinder(root) {
    if (!root || root.getAttribute('data-hp-finder-bound') === 'true') return;
    root.setAttribute('data-hp-finder-bound', 'true');

    var config = parseConfig(root);
    if (!config) return;

    var products = indexProducts(config.products);
    var modal = root.querySelector('[data-hp-finder-modal]');
    var dialog = root.querySelector('[data-hp-finder-dialog]');
    var overlay = root.querySelector('[data-hp-finder-overlay]');
    var closeBtns = root.querySelectorAll('[data-hp-finder-close]');
    var titleEl = root.querySelector('[data-hp-finder-title]');
    var progressEl = root.querySelector('[data-hp-finder-progress]');
    var nextBtn = root.querySelector('[data-hp-finder-next]');
    var backBtn = root.querySelector('[data-hp-finder-back]');
    var navEl = root.querySelector('[data-hp-finder-nav]');
    var resultEl = root.querySelector('[data-hp-finder-result]');
    var restartBtn = root.querySelector('[data-hp-finder-restart]');

    var state = {
      step: 1,
      answers: { 1: null, 2: null, 3: null },
      open: false,
      lastFocus: null,
    };

    function getFocusable() {
      return Array.prototype.slice
        .call(
          dialog.querySelectorAll(
            'a[href]:not([hidden]), button:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"])'
          )
        )
        .filter(function (el) {
          if (el.closest('[hidden]')) return false;
          return el.offsetParent !== null || el === dialog;
        });
    }

    function setAnswerSelected(step, value) {
      state.answers[step] = value;
      root.querySelectorAll('[data-hp-finder-answer][data-step="' + step + '"]').forEach(function (btn) {
        var selected = btn.getAttribute('data-hp-finder-answer') === value;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
      if (nextBtn) nextBtn.disabled = !value;
    }

    function showStep(step) {
      state.step = step;
      root.querySelectorAll('[data-hp-finder-step]').forEach(function (el) {
        var n = Number(el.getAttribute('data-hp-finder-step'));
        var active = n === step;
        el.hidden = !active;
        el.classList.toggle('is-active', active);
      });

      if (resultEl) resultEl.hidden = true;
      if (navEl) navEl.hidden = false;
      if (progressEl) {
        progressEl.hidden = false;
        progressEl.textContent = step + ' / 3';
      }
      if (titleEl) {
        titleEl.hidden = false;
        titleEl.textContent = (config.headings && config.headings[String(step)]) || '';
      }
      if (backBtn) backBtn.hidden = step === 1;
      if (nextBtn) {
        nextBtn.hidden = false;
        nextBtn.disabled = !state.answers[step];
        nextBtn.textContent = step === 3 ? 'See my match' : nextBtn.getAttribute('data-default-label') || 'Next';
      }
    }

    function resetQuiz() {
      state.answers = { 1: null, 2: null, 3: null };
      root.querySelectorAll('[data-hp-finder-answer]').forEach(function (btn) {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-checked', 'false');
      });
      showStep(1);
    }

    function rankEligible(flavor, personality, excludeDecaf) {
      var ids = Object.keys(SCORE).filter(function (id) {
        if (excludeDecaf && id === 'colombia_decaf') return false;
        if (id === 'colombia_decaf') return false;
        var product = products[id];
        return isMapped(product);
      });

      return ids
        .map(function (id) {
          return { id: id, score: scoreCoffee(id, flavor, personality), product: products[id] };
        })
        .sort(function (a, b) {
          var scoreCmp = compareCandidates(a, b, flavor);
          if (scoreCmp !== 0) return scoreCmp;
          var aAvail = isShopable(a.product) ? 0 : 1;
          var bAvail = isShopable(b.product) ? 0 : 1;
          return aAvail - bAvail;
        });
    }

    function pickDistinct(ranked, skipId) {
      for (var i = 0; i < ranked.length; i++) {
        if (skipId && ranked[i].id === skipId) continue;
        return ranked[i];
      }
      return null;
    }

    function recommend() {
      var flavor = state.answers[1];
      var personality = state.answers[2];
      var caffeine = state.answers[3];
      var primary = null;
      var secondary = null;

      if (caffeine === 'decaf') {
        primary = {
          id: 'colombia_decaf',
          score: 999,
          product: products.colombia_decaf,
        };
        var alts = rankEligible(flavor, personality, true);
        secondary = pickDistinct(alts, 'colombia_decaf');
      } else {
        var ranked = rankEligible(flavor, personality, true);
        primary = ranked[0] || null;
        secondary = pickDistinct(ranked, primary && primary.id);
      }

      if (primary && secondary && primary.id === secondary.id) {
        secondary = null;
      }

      return { primary: primary, secondary: secondary, flavor: flavor, personality: personality };
    }

    function renderProduct(target, candidate, flavor, personality, isPrimary) {
      var product = candidate && candidate.product;
      var id = candidate && candidate.id;
      var notes =
        (product && product.notes) || DEFAULT_NOTES[id] || '';
      var title = (product && product.title) || fallbackTitle(id);

      if (isPrimary) {
        root.querySelector('[data-hp-finder-result-eyebrow]').textContent =
          config.resultEyebrow || 'Your Slowpokes match';
        root.querySelector('[data-hp-finder-result-title]').textContent = title;
        root.querySelector('[data-hp-finder-result-notes]').textContent = notes;
        root.querySelector('[data-hp-finder-result-copy]').textContent = buildCopy(
          id,
          flavor,
          personality,
          notes
        );

        var media = root.querySelector('[data-hp-finder-result-media]');
        var img = root.querySelector('[data-hp-finder-result-image]');
        var cta = root.querySelector('[data-hp-finder-result-cta]');
        var unavailable = root.querySelector('[data-hp-finder-result-unavailable]');

        if (product && product.image) {
          img.src = product.image;
          img.alt = product.image_alt || title;
          media.hidden = false;
        } else {
          media.hidden = true;
          img.removeAttribute('src');
        }

        if (isShopable(product)) {
          cta.hidden = false;
          cta.href = product.url;
          cta.textContent = shopLabel(config.ctaPrefix, title);
          unavailable.hidden = true;
        } else {
          cta.hidden = true;
          cta.removeAttribute('href');
          unavailable.hidden = false;
        }
      } else {
        var also = root.querySelector('[data-hp-finder-also]');
        if (!candidate || !isMapped(product)) {
          also.hidden = true;
          return;
        }
        also.hidden = false;
        root.querySelector('[data-hp-finder-also-heading]').textContent =
          config.alsoHeading || 'You might also like';
        root.querySelector('[data-hp-finder-also-title]').textContent = title;
        root.querySelector('[data-hp-finder-also-notes]').textContent = notes;
        var alsoCta = root.querySelector('[data-hp-finder-also-cta]');
        if (isShopable(product)) {
          alsoCta.hidden = false;
          alsoCta.href = product.url;
          alsoCta.textContent = shopLabel(config.ctaPrefix, title);
        } else {
          alsoCta.hidden = true;
        }
      }
    }

    function fallbackTitle(id) {
      var map = {
        colombia_excelso: 'Colombia Excelso',
        guatemala: 'Guatemala',
        honduras: 'Honduras',
        brazil: 'Brazil',
        costa_rica: 'Costa Rica Tarrazu',
        mexico_chiapas: 'Mexico Chiapas',
        ethiopia: 'Ethiopia',
        colombia_decaf: 'Colombia Decaf',
      };
      return map[id] || 'Slowpokes Coffee';
    }

    function showResult() {
      var rec = recommend();
      state.step = 4;

      root.querySelectorAll('[data-hp-finder-step]').forEach(function (el) {
        el.hidden = true;
        el.classList.remove('is-active');
      });
      if (navEl) navEl.hidden = true;
      if (progressEl) progressEl.hidden = true;
      if (titleEl) titleEl.hidden = true;

      if (!rec.primary) {
        resultEl.hidden = false;
        root.querySelector('[data-hp-finder-result-title]').textContent = 'We’re still roasting';
        root.querySelector('[data-hp-finder-result-notes]').textContent = '';
        root.querySelector('[data-hp-finder-result-copy]').textContent =
          'Map coffees in the Theme Editor to turn on recommendations.';
        root.querySelector('[data-hp-finder-result-media]').hidden = true;
        root.querySelector('[data-hp-finder-result-cta]').hidden = true;
        root.querySelector('[data-hp-finder-also]').hidden = true;
        root.querySelector('[data-hp-finder-result-unavailable]').hidden = true;
        return;
      }

      resultEl.hidden = false;
      renderProduct(resultEl, rec.primary, rec.flavor, rec.personality, true);
      renderProduct(resultEl, rec.secondary, rec.flavor, rec.personality, false);

      window.requestAnimationFrame(function () {
        var focusables = getFocusable();
        if (focusables.length) focusables[0].focus();
      });
    }

    function openModal() {
      if (state.open) return;
      state.open = true;
      state.lastFocus = document.activeElement;
      resetQuiz();
      if (nextBtn && !nextBtn.getAttribute('data-default-label')) {
        nextBtn.setAttribute('data-default-label', nextBtn.textContent.trim() || 'Next');
      }
      modal.hidden = false;
      document.documentElement.classList.add('hp-coffee-finder-open');
      window.requestAnimationFrame(function () {
        modal.classList.add('is-visible');
        dialog.focus();
      });
    }

    function closeModal() {
      if (!state.open) return;
      state.open = false;
      modal.classList.remove('is-visible');
      document.documentElement.classList.remove('hp-coffee-finder-open');
      window.setTimeout(function () {
        modal.hidden = true;
        resetQuiz();
        if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
          state.lastFocus.focus();
        } else {
          var fallback = document.querySelector('[data-hp-finder-open]');
          if (fallback) fallback.focus();
        }
      }, 200);
    }

    document.querySelectorAll('[data-hp-finder-open]').forEach(function (btn) {
      if (btn.getAttribute('data-hp-finder-open-bound') === 'true') return;
      btn.setAttribute('data-hp-finder-open-bound', 'true');
      btn.addEventListener('click', openModal);
    });
    closeBtns.forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });
    if (overlay) overlay.addEventListener('click', closeModal);

    root.querySelectorAll('[data-hp-finder-answer]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = Number(btn.getAttribute('data-step'));
        var value = btn.getAttribute('data-hp-finder-answer');
        setAnswerSelected(step, value);
      });
    });

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!state.answers[state.step]) return;
        if (state.step < 3) {
          showStep(state.step + 1);
        } else {
          showResult();
        }
      });
    }

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (state.step > 1 && state.step <= 3) {
          showStep(state.step - 1);
        }
      });
    }

    if (restartBtn) {
      restartBtn.addEventListener('click', function () {
        resetQuiz();
        var focusables = getFocusable();
        if (focusables.length) focusables[0].focus();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (!state.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusables = getFocusable();
      if (!focusables.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function boot() {
    document.querySelectorAll('[data-hp-coffee-finder]').forEach(initFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event || !event.target) return;
    var root = event.target.querySelector('[data-hp-coffee-finder]');
    if (root) initFinder(root);
  });
})();
